import React, { useState, useEffect, useContext, useMemo } from 'react';
import { KeycloakContext } from '../App';
import { ServerStackIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { formatDate, formatDateTime } from '../lib/format';

interface AdminVps {
  id: number;
  user_id: string;
  offer_code?: string;
  offer_name?: string;
  vcores?: number;
  ram_gb?: number;
  ssd_gb?: number;
  price_ht?: number;
  price_ttc?: number;
  ip_address?: string;
  os?: string;
  username?: string;
  password?: string;
  protocol?: string;
  port?: number;
  location?: string;
  premium_backup?: boolean;
  snapshot_backup?: boolean;
  extra_storage_units?: number;
  auto_deploy?: boolean;
  label?: string;
  created_at?: string;
  updated_at?: string;
}

const LOCATIONS: Record<string, string> = { fr: 'Europe (France)', uk: 'Europe (UK)', pl: 'Europe (Poland)' };

export default function AdminVpsPage() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const [vpsList, setVpsList] = useState<AdminVps[]>([]);
  const [usersMap, setUsersMap] = useState<Record<string, { username: string; email: string }>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');

  // Edit modal
  const [editing, setEditing] = useState<AdminVps | null>(null);
  const [form, setForm] = useState<Partial<AdminVps>>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [revealCreds, setRevealCreds] = useState<boolean>(false);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [vpsRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/admin/vps`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
        fetch(`${API_URL}/admin/users`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      ]);
      if (!vpsRes.ok) throw new Error('Failed to fetch VPS');
      const data: AdminVps[] = await vpsRes.json();
      setVpsList(data || []);

      if (usersRes.ok) {
        const users = await usersRes.json();
        const map: Record<string, { username: string; email: string }> = {};
        (users || []).forEach((u: any) => {
          map[u.id] = { username: u.username || u.preferred_username || u.id, email: u.email || '' };
        });
        setUsersMap(map);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const userDisplay = (userId: string) => {
    const u = usersMap[userId];
    if (!u) return userId;
    return u.email ? `${u.username} (${u.email})` : u.username;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vpsList;
    return vpsList.filter((v) => {
      const u = usersMap[v.user_id];
      const blob = [
        v.label, v.offer_name, v.offer_code, v.os, v.ip_address, v.location, v.protocol,
        v.user_id, u?.username, u?.email,
      ].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [vpsList, usersMap, search]);

  const openEdit = (v: AdminVps) => {
    setEditing(v);
    setForm({ ...v });
    setRevealCreds(false);
  };

  const closeEdit = () => {
    setEditing(null);
    setForm({});
    setRevealCreds(false);
  };

  const buildPatch = (): Record<string, unknown> => {
    if (!editing) return {};
    const patch: Record<string, unknown> = {};
    const fields: (keyof AdminVps)[] = [
      'label', 'offer_code', 'offer_name', 'vcores', 'ram_gb', 'ssd_gb',
      'price_ht', 'price_ttc', 'ip_address', 'os', 'username', 'password',
      'protocol', 'port', 'location', 'premium_backup', 'snapshot_backup', 'extra_storage_units',
    ];
    for (const f of fields) {
      const next = form[f];
      const prev = editing[f];
      if (next !== prev && next !== undefined) {
        patch[f] = next;
      }
    }
    return patch;
  };

  const handleSave = async () => {
    if (!editing) return;
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      closeEdit();
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/admin/vps/${editing.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to update VPS');
      }
      const updated: AdminVps = await res.json();
      setVpsList((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
      toast('VPS updated', 'success');
      closeEdit();
    } catch (err: any) {
      toast(err.message || 'Failed to update', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (v: AdminVps) => {
    const ok = await confirm({
      title: 'Delete VPS',
      message: `Delete VPS #${v.id} (${v.offer_name || v.offer_code}) belonging to ${userDisplay(v.user_id)}? This removes the record from our database — provisioned infrastructure (VM, Terraform state) is not torn down.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/admin/vps/${v.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to delete VPS');
      }
      setVpsList((prev) => prev.filter((x) => x.id !== v.id));
      toast('VPS deleted', 'success');
    } catch (err: any) {
      toast(err.message || 'Failed to delete', 'error');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader icon={<ServerStackIcon />} title="VPS" description="Administer all user VPS instances." />
        <div className="rounded-lg border border-border bg-card p-6 space-y-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      </div>
    );
  }
  if (error) return <div className="rounded-md bg-destructive/10 p-4"><p className="text-sm text-destructive">Error: {error}</p></div>;

  return (
    <div className="space-y-6">
      <PageHeader icon={<ServerStackIcon />} title="VPS" description="Administer all user VPS instances." />

      <div className="bg-card shadow rounded-lg overflow-hidden">
        <div className="border-b border-border p-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by user, offer, label, IP, OS…"
              className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-2 text-sm focus-visible:ring-ring focus:border-primary"
            />
          </div>
          <span className="ml-auto text-xs text-muted-foreground">{filtered.length} of {vpsList.length}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Offer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Label</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Specs</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">IP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">OS</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-6 py-8 text-center text-sm text-muted-foreground">No VPS found.</td></tr>
              ) : filtered.map((v) => (
                <tr key={v.id} className="hover:bg-muted/40">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{userDisplay(v.user_id)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                    {v.offer_name || v.offer_code || '-'}
                    {v.auto_deploy && <span className="ml-1.5 inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">Auto-deployed</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{v.label || <span className="text-muted-foreground italic">—</span>}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{v.vcores}c · {v.ram_gb} GB · {v.ssd_gb} GB</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground font-mono">{v.ip_address || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{v.os || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{v.created_at ? formatDate(v.created_at) : '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(v)}
                        title="Edit"
                        className="p-1.5 rounded-md text-primary hover:bg-primary/10 cursor-pointer transition-colors"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(v)}
                        title="Delete"
                        className="p-1.5 rounded-md text-destructive hover:bg-destructive/10 cursor-pointer transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm" onClick={closeEdit} />
            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-card px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground">Edit VPS #{editing.id}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {editing.offer_name || editing.offer_code} · {userDisplay(editing.user_id)}
                      {editing.created_at && <> · created {formatDateTime(editing.created_at)}</>}
                    </p>
                  </div>
                  <button type="button" onClick={closeEdit} className="text-muted-foreground hover:text-foreground cursor-pointer" aria-label="Close">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Label" value={form.label ?? ''} onChange={(v) => setForm({ ...form, label: v })} />
                  <Field label="Offer name" value={form.offer_name ?? ''} onChange={(v) => setForm({ ...form, offer_name: v })} />
                  <Field label="Offer code" value={form.offer_code ?? ''} onChange={(v) => setForm({ ...form, offer_code: v })} />
                  <Field
                    label="Location"
                    value={form.location ?? ''}
                    onChange={(v) => setForm({ ...form, location: v })}
                    placeholder={Object.entries(LOCATIONS).map(([k, v]) => `${k}=${v}`).join(', ')}
                  />
                  <NumField label="vCores" value={form.vcores} onChange={(v) => setForm({ ...form, vcores: v })} />
                  <NumField label="RAM (GB)" value={form.ram_gb} onChange={(v) => setForm({ ...form, ram_gb: v })} />
                  <NumField label="SSD (GB)" value={form.ssd_gb} onChange={(v) => setForm({ ...form, ssd_gb: v })} />
                  <NumField label="Extra storage units" value={form.extra_storage_units} onChange={(v) => setForm({ ...form, extra_storage_units: v })} />
                  <NumField label="Price excl. VAT" value={form.price_ht} step="0.01" onChange={(v) => setForm({ ...form, price_ht: v })} />
                  <NumField label="Price incl. VAT" value={form.price_ttc} step="0.01" onChange={(v) => setForm({ ...form, price_ttc: v })} />
                  <Field label="IP address" value={form.ip_address ?? ''} onChange={(v) => setForm({ ...form, ip_address: v })} />
                  <Field label="OS" value={form.os ?? ''} onChange={(v) => setForm({ ...form, os: v })} />
                  <Field label="Protocol" value={form.protocol ?? ''} onChange={(v) => setForm({ ...form, protocol: v })} placeholder="SSH or RDP" />
                  <NumField label="Port" value={form.port} onChange={(v) => setForm({ ...form, port: v })} />

                  <BoolField label="Premium backup" value={form.premium_backup} onChange={(v) => setForm({ ...form, premium_backup: v })} />
                  <BoolField label="Snapshot backup" value={form.snapshot_backup} onChange={(v) => setForm({ ...form, snapshot_backup: v })} />

                  <div className="sm:col-span-2 mt-2 rounded-md border border-warning/30 bg-warning/5 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wider text-warning">Credentials</span>
                      <button
                        type="button"
                        onClick={() => setRevealCreds((v) => !v)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 cursor-pointer"
                      >
                        {revealCreds ? <><EyeSlashIcon className="h-3.5 w-3.5" /> Hide</> : <><EyeIcon className="h-3.5 w-3.5" /> Reveal</>}
                      </button>
                    </div>
                    {revealCreds && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Username" value={form.username ?? ''} onChange={(v) => setForm({ ...form, username: v })} />
                        <Field label="Password" value={form.password ?? ''} onChange={(v) => setForm({ ...form, password: v })} />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-muted/30 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
                <Button variant="outline" onClick={closeEdit} disabled={saving}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, className }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary"
      />
    </div>
  );
}

function NumField({ label, value, onChange, step, className }: { label: string; value: number | undefined; onChange: (v: number | undefined) => void; step?: string; className?: string }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</label>
      <input
        type="number"
        step={step}
        value={value ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === '' ? undefined : Number(v));
        }}
        className="mt-1 block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary"
      />
    </div>
  );
}

function BoolField({ label, value, onChange }: { label: string; value: boolean | undefined; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 mt-1 cursor-pointer">
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
      />
      <span className="text-sm text-foreground">{label}</span>
    </label>
  );
}
